import { HelpLink, Related, ScreenLink } from "./parts";

export function SigningInAndRoles() {
  return (
    <>
      <p>
        AllCode staff sign in with AWS IAM Identity Center, the same sign-in used for AWS. Your Identity Center groups
        decide your role, and your role decides which screens you see and what you can change.
      </p>

      <h2>Signing in</h2>
      <ol>
        <li>
          Open the admin app and choose <strong>Sign in</strong>, or open the <strong>Nexus admin</strong> tile in the AWS
          access portal.
        </li>
        <li>Sign in to Identity Center as usual. You come back to the page you started from.</li>
        <li>
          If none of your groups maps to a Nexus role, you see a “no access” page. Ask an administrator to add you to the
          right Identity Center group.
        </li>
      </ol>
      <p>
        The top bar shows the environment (a <strong>Dev</strong>, <strong>Stage</strong> or <strong>Local</strong> chip;
        production shows none), your name, your email and your role, and <strong>Sign out</strong>. In the audit trail you
        are recorded by a pseudonymous subject (<code>saml-</code> and a hash of your email), never by the email itself.
        The <ScreenLink href="/audit">Audit log</ScreenLink> screen shows the email next to it where the app knows it.
      </p>

      <h3>Theme</h3>
      <p>
        The app follows your computer's light or dark setting. The sun or moon button in the top bar switches to the
        other one; the choice is remembered in this browser only, and switching back to your computer's setting
        follows it again.
      </p>

      <h2 id="sessions">Sessions</h2>
      <ul>
        <li>
          A session lasts at most <strong>8 hours</strong> from sign-in and ends after <strong>30 minutes</strong> without a
          request.
        </li>
        <li>
          When it ends, the next action takes you back to Sign in, with a link back to the page you were on. Work you had
          not saved is lost, so save forms before a long break.
        </li>
        <li>
          <strong>Sign out</strong> ends the Nexus session. It does not sign you out of Identity Center or AWS.
        </li>
        <li>
          Your groups are read when you sign in. If an administrator adds or removes a group, sign out and in again to see
          the change. A super admin can end someone's sessions at once when access must stop now.
        </li>
      </ul>

      <h2 id="roles">Roles</h2>
      <table>
        <thead>
          <tr>
            <th>Role</th>
            <th>Group</th>
            <th>Can</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Super admin</td>
            <td>
              <code>nexus-super-admins</code>
            </td>
            <td>Everything, including customers and model prices.</td>
          </tr>
          <tr>
            <td>Billing viewer</td>
            <td>
              <code>nexus-billing-viewers</code>
            </td>
            <td>Read every billing and finance screen. No changes.</td>
          </tr>
          <tr>
            <td>Billing admin</td>
            <td>
              <code>nexus-billing-admins</code>
            </td>
            <td>
              Also edit rate plans and customers' billing, close months, and approve adjustments, usage-type mappings and
              AWS cost finalizing.
            </td>
          </tr>
          <tr>
            <td>Installer</td>
            <td>
              <code>nexus-installers</code>
            </td>
            <td>Link customer AWS accounts, run installer sessions and approve their changes.</td>
          </tr>
          <tr>
            <td>Customer admin</td>
            <td>
              <code>org-&lt;customer id&gt;-admins</code>
            </td>
            <td>Only Overview here. Customer admins manage their own company through the API.</td>
          </tr>
        </tbody>
      </table>
      <p>
        Groups combine: someone in both the billing admin and installer groups has both roles, and the top bar shows
        “Billing admin + Installer”. A customer admin group together with an AllCode staff group is refused rather than
        guessed at.
      </p>

      <h2 id="what-each-role-sees">What each role sees</h2>
      <table>
        <thead>
          <tr>
            <th>Screen</th>
            <th>Super admin</th>
            <th>Billing viewer / admin</th>
            <th>Installer</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Overview</td>
            <td>Yes (lands here)</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Customers</td>
            <td>Yes</td>
            <td>A customer's Billing tab and invoices, opened from Invoices</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Invoices, Rate plans, Model prices</td>
            <td>Yes</td>
            <td>Yes (lands on Invoices)</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Revenue &amp; margin, AWS costs, Reconciliation</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Installer</td>
            <td>Yes</td>
            <td>No</td>
            <td>Yes (lands here)</td>
          </tr>
          <tr>
            <td>Audit log</td>
            <td>Everything</td>
            <td>Billing changes</td>
            <td>Installer and AWS account changes</td>
          </tr>
          <tr>
            <td>Help</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
        </tbody>
      </table>
      <p>
        A billing viewer sees the same screens as a billing admin, with the buttons that change things left out. Only super
        admins can add or change model prices. A link to a screen your role cannot open shows “not available to your
        role”.
      </p>

      <Related to={["what-nexus-does", "audit-log", "glossary"]} />
    </>
  );
}
